import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldQuestion, RefreshCw } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }) : "—");

const LABEL = { bisu: "Token Embedded Signup (BISU)", suat: "System User Access Token" };

function expiryText(c) {
  if (c.expiry_status === "none_confirmed") return "Nessuna scadenza programmata (confermato da Meta)";
  if (c.expiry_status === "confirmed") return `Scadenza confermata da Meta: ${fmt(c.expires_at)}`;
  if (c.expiry_status === "declared") {
    return c.expiry_kind === "non_expiring"
      ? "Dichiarata senza scadenza dall'admin (non verificata da Meta)"
      : `Scadenza dichiarata dall'admin: ${fmt(c.expires_at)} (non verificata da Meta)`;
  }
  return "Scadenza non verificata";
}

function CredentialRow({ c }) {
  const verified = c.expiry_status === "none_confirmed" || c.expiry_status === "confirmed";
  const Icon = verified ? ShieldCheck : ShieldQuestion;
  return (
    <div className="rounded border border-ink-200 p-3 text-sm" data-testid={`wa-cred-${c.source}`}>
      <div className="flex items-center gap-2 font-medium text-ink-900">
        <Icon className={`w-4 h-4 ${verified ? "text-emerald-600" : "text-amber-600"}`} />
        {LABEL[c.source] || c.source}
        {c.active && <span className="chip border-emerald-500 text-emerald-700 bg-emerald-50" data-testid={`wa-cred-${c.source}-active`}>In uso</span>}
        {c.meta_is_valid === false && <span className="chip border-red-400 text-red-700 bg-red-50">Meta: non valido</span>}
      </div>
      <div className="mt-1 text-ink-700" data-testid={`wa-cred-${c.source}-expiry`}>{expiryText(c)}</div>
      {verified && (
        <div className="mt-1 text-xs text-ink-500" data-testid={`wa-cred-${c.source}-meta`}>
          Tipo: {c.meta_token_type || "—"} · Verificato il {fmt(c.meta_verified_at)}
          {c.meta_data_access_expires_at ? ` · Accesso dati fino al ${fmt(c.meta_data_access_expires_at)}` : " · Nessuna scadenza accesso dati"}
        </div>
      )}
      {c.legacy_synthetic_expiry_ignored && (
        <div className="mt-1 text-xs text-ink-500" data-testid={`wa-cred-${c.source}-legacy`}>
          La data calcolata alla connessione (+60 giorni) non è una scadenza Meta ed è ignorata.
        </div>
      )}
    </div>
  );
}

export function CredentialStatusPanel({ credentials, onVerified }) {
  const [busy, setBusy] = useState(false);
  if (!credentials || credentials.length === 0) return null;
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/messaging/whatsapp/credentials/verify");
      const errs = Object.entries(data.results || {}).filter(([, r]) => !r.ok);
      if (errs.length) toast.error(`Verifica Meta non riuscita: ${errs.map(([s, r]) => `${s}: ${r.error}`).join("; ")}`);
      else toast.success("Credenziali verificate con Meta");
      onVerified?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-2" data-testid="wa-credentials-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-ink-900">Credenziale WhatsApp</div>
        <button type="button" className="btn-outline text-xs flex items-center gap-1" onClick={verify}
                disabled={busy} data-testid="wa-cred-verify-btn">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Verifica con Meta
        </button>
      </div>
      {credentials.map((c) => <CredentialRow key={c.source} c={c} />)}
    </div>
  );
}
